import { useState } from "react";
import { api } from "../lib/api";
import { AlertTriangle, Loader2, Database, FileText, Users, Building2, MessageCircle, Film, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * AdminMaintenance
 *
 * Single control surface for all destructive marketplace operations.
 *
 * Three independent actions are exposed here, each behind its own
 * dry-run preview + typed-confirmation gate ("type DELETE to commit").
 * The typed-gate ensures the operator can never accidentally trigger
 * a destructive call via a misclick — they have to literally type the
 * word DELETE into the action's input.
 *
 * Why one screen instead of three pages:
 *   These are the rare-use, scary-button operations. Putting them all
 *   in one place — with consistent dry-run + confirm UX — means the
 *   operator only has to learn the pattern once and can't confuse
 *   "delete services" with "purge test content" mid-action.
 *
 * Actions:
 *   1. Delete all services           → services only (preserves reviews)
 *   2. Reset services + reviews      → wipes both
 *   3. Purge test content            → test users + cascade across 40+ collections
 */
const ACTIONS = [
  {
    id: "services",
    title: "Delete all services",
    icon: FileText,
    endpoint: "/admin/delete-all-services",
    description:
      "Removes every Service listing. Preserves reviews, bookings, provider profiles, and users.",
    summarizeDryRun: (data) =>
      `${data?.willDelete?.services ?? 0} services would be deleted.`,
    summarizeCommit: (data) =>
      `Deleted ${data?.deletedServices ?? 0} services. Cleared cache on ${data?.providerCacheCleared ?? 0} provider profiles.`,
  },
  {
    id: "temples",
    title: "Delete all temples",
    icon: Building2,
    endpoint: "/admin/delete-all-temples",
    description:
      "Removes every Temple plus all temple-scoped activity (inbox state, messages, treasury log). Preserves users, services, posts.",
    summarizeDryRun: (data) => {
      const d = data?.willDelete || {};
      return `${d.temples ?? 0} temples · ${d.temple_messages ?? 0} messages · ${d.temple_treasury_log ?? 0} treasury entries · ${d.temple_inbox_state ?? 0} inbox rows.`;
    },
    summarizeCommit: (data) => {
      const d = data?.deleted || {};
      return `Deleted ${d.temples ?? 0} temples + ${d.temple_messages ?? 0} messages + ${d.temple_treasury_log ?? 0} treasury + ${d.temple_inbox_state ?? 0} inbox rows.`;
    },
  },
  {
    id: "communities",
    title: "Delete all communities",
    icon: MessageCircle,
    endpoint: "/admin/delete-all-communities",
    description:
      "Removes every Community plus every membership row. Preserves users and their posts (orphan-tolerant by design).",
    summarizeDryRun: (data) => {
      const d = data?.willDelete || {};
      return `${d.communities ?? 0} communities · ${d.memberships ?? 0} memberships.`;
    },
    summarizeCommit: (data) => {
      const d = data?.deleted || {};
      return `Deleted ${d.communities ?? 0} communities + ${d.memberships ?? 0} memberships.`;
    },
  },
  {
    id: "posts",
    title: "Delete all posts & clips",
    icon: Film,
    endpoint: "/admin/delete-all-posts",
    description:
      "Removes every Post (feed entry, image, video clip) plus all comments and moderation reports. Preserves users, services, communities.",
    summarizeDryRun: (data) => {
      const d = data?.willDelete || {};
      return `${d.posts ?? 0} posts/clips · ${d.comments ?? 0} comments · ${d.post_reports ?? 0} reports.`;
    },
    summarizeCommit: (data) => {
      const d = data?.deleted || {};
      return `Deleted ${d.posts ?? 0} posts/clips + ${d.comments ?? 0} comments + ${d.post_reports ?? 0} reports.`;
    },
  },
  {
    id: "services-reviews",
    title: "Reset services AND reviews",
    icon: Database,
    endpoint: "/admin/reset-services-and-reviews",
    description:
      "Removes every Service AND every Review. Also unsets cached rating aggregates.",
    summarizeDryRun: (data) =>
      `${data?.willDelete?.services ?? 0} services + ${data?.willDelete?.reviews ?? 0} reviews would be deleted.`,
    summarizeCommit: (data) =>
      `Deleted ${data?.deletedServices ?? 0} services + ${data?.deletedReviews ?? 0} reviews. Cleared cache on ${data?.providerCacheCleared ?? 0} provider profiles.`,
  },
  {
    id: "purge-test",
    title: "Purge all test content",
    icon: Users,
    endpoint: "/admin/purge-test-content",
    description:
      "Finds test users (test@/demo@/example.com etc.) and cascade-deletes all their owned content across 40+ collections. Operator + brand emails are always protected.",
    summarizeDryRun: (data) => {
      const totals = data?.wouldDelete || {};
      const top = Object.entries(totals)
        .filter(([k]) => k !== "__isDemo_flagged")
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      return `${data?.testUsersFound ?? 0} test users found. Top cascading: ${top || "(none)"}.`;
    },
    summarizeCommit: (data) => {
      const totals = data?.deleted || {};
      const top = Object.entries(totals)
        .filter(([k]) => k !== "__isDemo_flagged")
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
      return `Deleted ${data?.testUsersDeleted ?? 0} test users + cascade: ${top || "(none)"}.`;
    },
  },
  {
    id: "orphan-content",
    title: "Purge orphan posts & communities",
    icon: Database,
    endpoint: "/admin/cleanup-orphan-content",
    description:
      "Removes posts, comments, and communities whose author user no longer exists in the users collection. Fixes feed entries that render the 'Cauldron Member' fallback because the real user was previously deleted.",
    summarizeDryRun: (data) => {
      const d = data?.willDelete || {};
      return `${d.posts ?? 0} orphan posts · ${d.comments_by_dead_author ?? 0} orphan comments · ${d.communities ?? 0} orphan communities · ${d.memberships ?? 0} memberships.`;
    },
    summarizeCommit: (data) => {
      const d = data?.deleted || {};
      return `Deleted ${d.posts ?? 0} posts + ${d.comments ?? 0} comments + ${d.communities ?? 0} communities + ${d.memberships ?? 0} memberships.`;
    },
  },
];

export default function AdminMaintenance() {
  return (
    <div className="space-y-6" data-testid="admin-maintenance">
      <div>
        <h2 className="font-serif text-xl">Maintenance</h2>
        <p className="text-sm text-foreground/60 mt-1">
          Destructive operations. Each is dry-run by default, then gated by a typed confirmation.
        </p>
      </div>

      <div className="rounded-sm border border-red-500/30 bg-red-950/20 p-4 flex items-start gap-3">
        <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
        <div className="text-sm text-red-200/85 leading-relaxed">
          <b>These actions are irreversible.</b> Always run the dry-run first, review the counts carefully, and only commit once you&apos;re certain. Operator + brand emails are protected on all actions.
        </div>
      </div>

      <div className="space-y-4">
        {ACTIONS.map((a) => (
          <DestructiveCard key={a.id} action={a} />
        ))}
      </div>
    </div>
  );
}

function DestructiveCard({ action }) {
  const Icon = action.icon;
  const [dryRunData, setDryRunData] = useState(null);
  const [committedData, setCommittedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const reset = () => {
    setDryRunData(null);
    setCommittedData(null);
    setConfirmText("");
  };

  const dryRun = async () => {
    setLoading(true);
    setCommittedData(null);
    try {
      const { data } = await api.post(action.endpoint, { confirm: false });
      setDryRunData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type "DELETE" to confirm');
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(action.endpoint, { confirm: true });
      setCommittedData(data);
      setDryRunData(null);
      setConfirmText("");
      toast.success("Action committed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Commit failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="border border-secondary/15 rounded-sm overflow-hidden"
      data-testid={`maint-card-${action.id}`}
    >
      <div className="bg-background/60 p-5 flex items-start gap-4">
        <div className="shrink-0 w-10 h-10 rounded bg-red-500/10 text-red-300 flex items-center justify-center">
          <Icon size={18} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-lg leading-tight">{action.title}</h3>
          <p className="text-sm text-foreground/65 mt-1 leading-relaxed">{action.description}</p>

          {/* ─── Idle state — show only Dry Run button ─── */}
          {!dryRunData && !committedData && (
            <div className="mt-4">
              <button
                onClick={dryRun}
                disabled={loading}
                className="inline-flex items-center gap-2 border border-secondary/40 text-secondary px-4 py-2 rounded-sm text-xs uppercase tracking-[0.18em] hover:bg-secondary/10 disabled:opacity-40"
                data-testid={`maint-dry-run-${action.id}`}
              >
                {loading ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={12} />}
                Dry run
              </button>
            </div>
          )}

          {/* ─── Dry run results — show preview + typed-confirm gate ─── */}
          {dryRunData && (
            <div className="mt-4 space-y-3" data-testid={`maint-dry-result-${action.id}`}>
              <div className="parchment border border-amber-500/30 bg-amber-950/10 rounded-sm p-3 text-sm text-amber-100/90">
                {action.summarizeDryRun(dryRunData)}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground/55">Type</span>
                <span className="font-mono text-xs bg-secondary/15 text-secondary px-2 py-0.5 rounded">DELETE</span>
                <span className="text-xs text-foreground/55">to enable commit:</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="bg-background border border-red-500/40 rounded-sm px-3 py-2 text-sm font-mono w-40 focus:outline-none focus:border-red-500"
                  data-testid={`maint-confirm-input-${action.id}`}
                />
                <button
                  onClick={commit}
                  disabled={loading || confirmText.trim().toUpperCase() !== "DELETE"}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-sm text-xs uppercase tracking-[0.18em] disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`maint-commit-${action.id}`}
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                  Commit
                </button>
                <button
                  onClick={reset}
                  disabled={loading}
                  className="text-xs uppercase tracking-[0.18em] text-foreground/55 hover:text-foreground px-3 py-2"
                  data-testid={`maint-cancel-${action.id}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* ─── Committed — show result ─── */}
          {committedData && (
            <div className="mt-4 space-y-3" data-testid={`maint-commit-result-${action.id}`}>
              <div className="border border-emerald-500/30 bg-emerald-950/20 rounded-sm p-3 text-sm text-emerald-100/90">
                ✓ {action.summarizeCommit(committedData)}
              </div>
              <button
                onClick={reset}
                className="text-xs uppercase tracking-[0.18em] text-foreground/55 hover:text-foreground"
                data-testid={`maint-reset-${action.id}`}
              >
                Run again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
